import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Paper,
  Box,
  Avatar,
  Typography,
  Grid,
  Link,
  Breadcrumbs,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as EditIcon } from '../../../../assets/images/edit.svg';
import { ReactComponent as WrongIcon } from '../../../../assets/images/wrong.svg';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomModal } from '../../../../components/CustomModal';
import { ROUTES, getRouteWithParam, ROUTE_FOR_ASSETS } from '../../../../constants/routes';
import { getUserById } from '../../../../store/user/actions';
import { deactivateUser, activateUser } from '../../../../store/user/actions';
import { repeatInviteUser } from '../../../../store/user/actions';
import { serviceRoles } from '../../../../utils/serviceRoles';
import { StatusColor } from '../../../../components/StatusColor';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

export const ProfileTeamPerson = () => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const pathname = useLocation().pathname;

  const id = pathname.substr(pathname.lastIndexOf('/') + 1);

  const openAlert = () => {
    setOpen(true);
    setTimeout(() => setOpen(false), 2000);
  };

  const userState = useSelector((state) => state.user);
  const { info: userInfo, fetching } = userState;
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);
  const { userData } = useSelector((state) => state.currentUser);

  const isUpdateNotVisible = !visibleBySectionPermission(permissions, PERMISSIONS.USER.UpdateUsers.value);
  const idDisabledDeactivateUsers = !visibleBySectionPermission(permissions, PERMISSIONS.USER.DeactivateUsers.value);
  const idDisabledActivateUsers = !visibleBySectionPermission(permissions, PERMISSIONS.USER.ActivateUsers.value);
  const idDisabledInviteUsers = !visibleBySectionPermission(permissions, PERMISSIONS.USER.InviteUsers.value);
  const isPageVisible =
    visibleBySectionPermission(permissions, [PERMISSIONS.USER.ListUsers.value, PERMISSIONS.USER.CreateUsers.value], 'or') &&
    visibleBySectionPermission(permissions, PERMISSIONS.USER.RetrieveUsers.value);

  useEffect(() => {
    dispatch(getUserById(id));
  }, [dispatch, id]);

  const [isModalOpenedActivate, setIsModalOpenedActivate] = useState(false);
  const [isModalOpenedDeactivate, setIsModalOpenedDeactivate] = useState(false);

  const handleActiveUser = () => {
    setIsModalOpenedActivate(false);
    dispatch(activateUser(id));
  };

  const handleDeactivateUser = () => {
    setIsModalOpenedDeactivate(false);
    dispatch(deactivateUser(id));
  };

  const handlerInviteUser = () => {
    dispatch(repeatInviteUser(userInfo.id, openAlert));
  };

  if (!isPageVisible) {
    return <></>;
  }

  return (
    <>
      {fetching ? (
        <Box
          sx={{
            marginTop: '150px',
            textAlign: 'center'
          }}>
          <CircularProgress data-testid="CircularProgress" />
        </Box>
      ) : (
        <>
          <Breadcrumbs mb="16px" aria-label="breadcrumb">
            <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.TEAM}>
              Команда
            </Link>
            <Typography color="text.secondary">
              {userInfo?.firstName} {userInfo?.lastName}
            </Typography>
          </Breadcrumbs>

          <Paper
            sx={{
              padding: '32px',
              marginBottom: '16px'
            }}>
            <Box
              sx={{
                display: 'flex'
              }}>
              <Avatar
                alt={`${userInfo.firstName} ${userInfo.lastName}`}
                src={`${ROUTE_FOR_ASSETS}${userInfo.avatar}`}
                sx={{ width: 128, height: 128, marginRight: '32px' }}
              />

              <Box
                sx={{
                  width: '100%',
                  display: 'flex'
                }}>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '510px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <Typography variant="h1" mb="8px">
                    {userInfo?.lastName} {userInfo?.firstName} {userInfo?.middleName}
                  </Typography>
                  <Typography variant="subtitle2" component="p" mb="24px">
                    {serviceRoles(userInfo.role?.id).label}
                  </Typography>
                  <Box sx={{ display: 'flex' }}>
                    <Box>
                      <CustomButton
                        color="secondary"
                        variant="outlined"
                        size="small"
                        sx={{ marginRight: '16px' }}
                        startIcon={<EditIcon />}
                        component={RouterLink}
                        to={userData.id === id ? ROUTES.EDIT_PROFILE : getRouteWithParam(ROUTES.TEAM_EDIT_PROFILE_PERSON, ':id', id)}
                        disabled={isUpdateNotVisible}
                        tooltip={isUpdateNotVisible ? DISABLED_CONTROL_TOOLTIP : null}
                        data-testid="EditButton"
                      >
                        Редактировать
                      </CustomButton>
                    </Box>
                    {userInfo.status === 'Активен' && (
                      <CustomButton
                        color="error"
                        variant="outlined"
                        size="small"
                        startIcon={<WrongIcon />}
                        onClick={() => setIsModalOpenedDeactivate(true)}
                        disabled={idDisabledDeactivateUsers}
                        tooltip={idDisabledDeactivateUsers ? DISABLED_CONTROL_TOOLTIP : null}
                        data-testid="DeactivateButton"
                      >
                        Заблокировать
                      </CustomButton>
                    )}
                    {userInfo.status === 'Заблокирован' && (
                      <CustomButton
                        color="success"
                        variant="outlined"
                        size="small"
                        startIcon={<WrongIcon color="success" />}
                        onClick={() => setIsModalOpenedActivate(true)}
                        disabled={idDisabledActivateUsers}
                        tooltip={idDisabledActivateUsers ? DISABLED_CONTROL_TOOLTIP : null}
                        data-testid="ActivateButton"
                      >
                        Разблокировать
                      </CustomButton>
                    )}
                    {(userInfo.status === 'Активен' || userInfo.status === 'Заблокирован') ? null : (
                      <CustomButton
                        color="secondary"
                        variant="outlined"
                        size="small"
                        sx={{
                          marginLeft: userInfo.status === 'Приглашен' ? '0px' : '16px'
                        }}
                        onClick={handlerInviteUser}
                        disabled={open || idDisabledInviteUsers}
                        tooltip={idDisabledInviteUsers ? DISABLED_CONTROL_TOOLTIP : null}
                        data-testid="InviteButton"
                      >
                        Пригласить
                      </CustomButton>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    textAlign: 'end'
                  }}>
                  <StatusColor status={userInfo.status} variant="subtitle2" />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper
            sx={{
              padding: '24px'
            }}>
            <Grid container>
              <Grid item xs={3}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <Typography variant="body1" color="text.secondary">
                    Номер телефона
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500
                    }}>
                    {userInfo.phone && `+7${userInfo.phone}`}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <Typography variant="body1" color="text.secondary">
                    Email
                  </Typography>
                  <Link
                    href="mailto:myemail@gmail.com"
                    variant="subtitle2"
                    color="primary.main"
                    underline="hover"
                    sx={{
                      fontWeight: 500
                    }}>
                    {userInfo.email}
                  </Link>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <Typography variant="body1" color="text.secondary">
                    Филиал
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500
                    }}>
                    {userInfo?.branch?.name}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <Typography variant="body1" color="text.secondary">
                    Департамент
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500
                    }}>
                    {userInfo?.department?.name}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      <Snackbar
        open={open}
        autoHideDuration={1000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          severity="success"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '250px',
            height: '100px'
          }}>
          Приглашение отправлено
        </Alert>
      </Snackbar>

      <CustomModal
        opened={isModalOpenedActivate}
        modalClose={() => setIsModalOpenedActivate(false)}
        modalSubmit={handleActiveUser}
        text={`Вы уверены, что хотите разблокировать ${userInfo.firstName} ${userInfo.lastName}?`}
        cancelBtnName="Отмена"
        submitBtnName="Да"
        data-testid="ActivateModal"
      />
      <CustomModal
        opened={isModalOpenedDeactivate}
        modalClose={() => setIsModalOpenedDeactivate(false)}
        modalSubmit={handleDeactivateUser}
        text={`Вы уверены, что хотите заблокировать ${userInfo.firstName} ${userInfo.lastName}?`}
        cancelBtnName="Отмена"
        submitBtnName="Да"
        data-testid="BlockModal"
      />
    </>
  );
};
